import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { LifeBuoy, Plus } from 'lucide-react';

const STATUS_LABELS = { open: 'Abierto', in_progress: 'En progreso', resolved: 'Resuelto', closed: 'Cerrado' };
const STATUS_COLORS = {
  open: 'bg-red-50 text-red-600 border-red-200',
  in_progress: 'bg-amber-50 text-amber-600 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  closed: 'bg-slate-50 text-slate-500 border-slate-200',
};
const PRIORITY_LABELS = { low: 'Baja', medium: 'Media', high: 'Alta', urgent: 'Urgente' };
const PRIORITY_COLORS = {
  low: 'text-[#94A3B8]', medium: 'text-[#64748B]', high: 'text-amber-600', urgent: 'text-red-600 font-semibold',
};

export default function SupportTickets() {
  const [searchParams] = useSearchParams();
  const urlTenantId = searchParams.get('tenant_id');
  const urlTicketId = searchParams.get('ticket_id');

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const [selected, setSelected] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [createForm, setCreateForm] = useState({ tenant_id: '', subject: '', description: '', priority: 'medium', category: '' });
  const [creating, setCreating] = useState(false);

  const fetchTickets = async (status = statusFilter, priority = priorityFilter) => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (status !== 'all') params.status = status;
      if (priority !== 'all') params.priority = priority;
      if (urlTenantId) params.tenant_id = urlTenantId;
      const { data } = await api.get('/admin/tickets', { params });
      setTickets(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchTickets();
    if (urlTicketId) {
      api.get(`/admin/tickets/${urlTicketId}`)
        .then(({ data }) => { setSelected(data); setComments(data.comments || []); })
        .catch(() => {});
    }
  }, []);

  const openTicket = async (ticket) => {
    try {
      const { data } = await api.get(`/admin/tickets/${ticket.id}`);
      setSelected(data);
      setComments(data.comments || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleUpdate = async (field, value) => {
    setUpdating(true);
    try {
      const { data } = await api.patch(`/admin/tickets/${selected.id}`, { [field]: value });
      setSelected({ ...selected, ...data });
      fetchTickets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUpdating(false); }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSavingComment(true);
    try {
      const { data } = await api.post(`/admin/tickets/${selected.id}/comments`, { comment: newComment.trim() });
      setComments([...comments, data]);
      setNewComment('');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingComment(false); }
  };

  const openCreateDialog = async () => {
    if (tenants.length === 0) {
      try {
        const { data } = await api.get('/admin/tenants');
        setTenants(data);
      } catch (e) { /* best-effort */ }
    }
    setShowCreate(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/tickets', createForm);
      setShowCreate(false);
      setCreateForm({ tenant_id: '', subject: '', description: '', priority: 'medium', category: '' });
      fetchTickets();
      toast.success('Ticket creado');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  if (selected) {
    return (
      <div data-testid="ticket-detail">
        <button onClick={() => setSelected(null)} className="text-xs text-[#94A3B8] hover:text-[#0F172A] mb-3 block">&larr; Volver a tickets</button>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-[#0F172A]">{selected.subject}</h1>
            <p className="text-xs text-[#64748B] mt-1">{selected.tenant_name} · creado por {selected.created_by_name || 'desconocido'}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
              <p className="text-sm text-[#334155] whitespace-pre-wrap">{selected.description || 'Sin descripción'}</p>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Comentarios</h3>
              <div className="space-y-3 mb-4" data-testid="ticket-comments">
                {comments.length === 0 ? (
                  <p className="text-xs text-[#94A3B8] text-center py-3">Sin comentarios</p>
                ) : comments.map(c => (
                  <div key={c.id} className="border-l-2 border-[#E2E8F0] pl-3 py-1">
                    <p className="text-sm text-[#0F172A]">{c.comment}</p>
                    <p className="text-[11px] text-[#94A3B8]">{c.author_name || 'Desconocido'} · {c.created_at ? new Date(c.created_at).toLocaleString('es-MX') : ''}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={handleAddComment} className="flex gap-2">
                <Input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Agregar un comentario..."
                  className="bg-white border-[#E2E8F0] text-sm" data-testid="new-comment-input" />
                <Button type="submit" disabled={savingComment || !newComment.trim()} className="bg-[#0F172A] text-white hover:bg-[#1E293B]">
                  {savingComment ? 'Enviando...' : 'Enviar'}
                </Button>
              </form>
            </div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 h-fit space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-1 block">Estado</Label>
              <Select value={selected.status} onValueChange={v => handleUpdate('status', v)} disabled={updating}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-1 block">Prioridad</Label>
              <Select value={selected.priority} onValueChange={v => handleUpdate('priority', v)} disabled={updating}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selected.category && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-1 block">Categoría</Label>
                <p className="text-sm text-[#334155] capitalize">{selected.category}</p>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-1 block">Creado</Label>
              <p className="text-xs text-[#94A3B8]">{selected.created_at ? new Date(selected.created_at).toLocaleString('es-MX') : ''}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="support-tickets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Soporte</h1>
          <p className="text-sm text-[#64748B] mt-1">Tickets internos de todos los tenants</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="new-ticket-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo ticket
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); fetchTickets(v, priorityFilter); }}>
          <SelectTrigger className="w-44 bg-white border-[#E2E8F0] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={v => { setPriorityFilter(v); fetchTickets(statusFilter, v); }}>
          <SelectTrigger className="w-44 bg-white border-[#E2E8F0] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toda prioridad</SelectItem>
            {Object.entries(PRIORITY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /></div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-16 text-[#94A3B8]">
            <LifeBuoy className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
            <p className="text-sm">Sin tickets</p>
          </div>
        ) : (
          <div className="divide-y divide-[#F1F5F9]" data-testid="tickets-list">
            {tickets.map(t => (
              <div key={t.id} onClick={() => openTicket(t)}
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#F8F9FA]" data-testid={`ticket-row-${t.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0F172A] truncate">
                    {t.display_code && <span className="font-mono font-normal text-[#94A3B8] mr-1.5">{t.display_code}</span>}
                    {t.subject}
                  </p>
                  <p className="text-xs text-[#94A3B8]">{t.tenant_name} · {t.assigned_to_name ? `asignado a ${t.assigned_to_name}` : 'sin asignar'}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  <span className={`text-xs ${PRIORITY_COLORS[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" data-testid="create-ticket-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A]">Nuevo Ticket</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Tenant</Label>
              <Select value={createForm.tenant_id} onValueChange={v => setCreateForm({ ...createForm, tenant_id: v })}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="create-ticket-tenant"><SelectValue placeholder="Selecciona un tenant" /></SelectTrigger>
                <SelectContent>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Asunto</Label>
              <Input value={createForm.subject} onChange={e => setCreateForm({ ...createForm, subject: e.target.value })} required
                className="bg-white border-[#E2E8F0] text-sm" data-testid="create-ticket-subject" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Descripción</Label>
              <Input value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
                className="bg-white border-[#E2E8F0] text-sm" data-testid="create-ticket-description" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Prioridad</Label>
              <Select value={createForm.priority} onValueChange={v => setCreateForm({ ...createForm, priority: v })}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button type="submit" disabled={creating || !createForm.tenant_id || !createForm.subject}
                className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="submit-create-ticket">
                {creating ? 'Creando...' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
